import axios, { type InternalAxiosRequestConfig, type AxiosResponse, type AxiosError } from 'axios';
import { cryptoCache } from './cryptoCache';

export const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://admin.crypxa.com';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('access_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.url?.includes('/api/dex/')) {
      const method = config.method?.toUpperCase() ?? '';
      const params = config.params ? ` ${JSON.stringify(config.params)}` : '';
      console.log(`[DEX] ${method} ${config.url}${params}`);
    }
    return config;
  },
  (error: unknown) => Promise.reject(error)
);

// Handle token refresh on 401
api.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response.config.url?.includes('/api/dex/')) {
      console.log(`[DEX] ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`);
    }
    return response;
  },
  async (error: AxiosError) => {
    if (error.config?.url?.includes('/api/dex/')) {
      console.warn('[DEX] error', error.config.method?.toUpperCase(), error.config.url, error.response?.status, error.message);
    }
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
      const response = await axios.post(`${API_BASE_URL}/api/token/refresh/`, {
        refresh: refreshToken,
      });
          const { access, refresh } = response.data;
          localStorage.setItem('access_token', access);
          // Update refresh token if rotation is enabled (new refresh token is returned)
          if (refresh) {
            localStorage.setItem('refresh_token', refresh);
          }
          originalRequest.headers.Authorization = `Bearer ${access}`;
          return api(originalRequest);
        } catch (err) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
        }
      } else {
        // No refresh token, redirect to login
        localStorage.removeItem('access_token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export interface CryptoListingParams {
  start?: string;
  limit?: string;
  convert?: string;
  sort?: string;
  sort_dir?: string;
}

export interface CryptoQuoteParams {
  convert?: string;
}

export interface HistoricalParams {
  time_start?: string;
  time_end?: string;
  interval?: '1d' | '1h' | '5m' | '1m';
  convert?: string;
}

export interface OHLCVParams {
  days?: string;
  interval?: '1d' | '1h' | '5m' | '1m';
  convert?: string;
  time_start?: string;
  time_end?: string;
}

/** Used by getMarketChart / chart to avoid JSX parse issues with inline > in .tsx */
export interface MarketChartQuoteItem {
  time_close?: string;
  quote?: { USD?: { close?: number } };
}
export interface MarketChartResponse {
  data?: Record<string, { quotes?: MarketChartQuoteItem[] }>;
}

export const cryptoAPI = {
  // Listings with caching to prevent duplicate API calls
  getListings: async (params: CryptoListingParams = {}) => {
    // Check cache first (2 minutes cache)
    const cached = cryptoCache.get(params);
    if (cached) {
      return { data: cached };
    }

    // Check if same request is already pending
    if (cryptoCache.hasPendingRequest(params)) {
      const pending = cryptoCache.getPendingRequest(params);
      if (pending) {
        // Reusing pending API request
        return pending;
      }
    }

    // Make new request
    const request = api.get('/api/crypto/listings/', { params })
      .then((response: AxiosResponse) => {
        cryptoCache.set(params, response.data);
        return response;
      })
      .catch((error: AxiosError) => {
        throw error;
      });

    // Track pending request
    cryptoCache.setPendingRequest(params, request);
    
    return request;
  },
  
  getQuotes: async (symbol: string, params: CryptoQuoteParams = {}) => {
    // Check cache first (1 minute cache for quotes)
    const cached = cryptoCache.getQuote(symbol);
    if (cached) {
      return { data: cached };
    }

    // Check if same request is already pending
    if (cryptoCache.hasPendingQuote(symbol)) {
      const pending = cryptoCache.getPendingQuote(symbol);
      if (pending) {
        console.log(`⏳ Reusing pending quote request for ${symbol}`);
        return pending;
      }
    }

    // Make new request
    const request = api.get(`/api/crypto/quotes/${symbol}/`, { params })
      .then((response: AxiosResponse) => {
        cryptoCache.setQuote(symbol, response.data);
        return response;
      })
      .catch((error: AxiosError) => {
        throw error;
      });

    cryptoCache.setPendingQuote(symbol, request);
    return request;
  },
  
  getInfo: (symbol: string) => {
    return api.get(`/api/crypto/info/${symbol}/`);
  },
  
  getTrending: () => {
    return api.get('/api/crypto/trending/');
  },
  
  // Historical Data & Charts
  getHistoricalData: (symbol: string, params: HistoricalParams = {}) => {
    return api.get(`/api/crypto/historical/${symbol}/`, { params });
  },
  
  getOHLCVData: (symbol: string, params: OHLCVParams = {}) => {
    return api.get(`/api/crypto/ohlcv/${symbol}/`, { params });
  },

  getMarketChart: (symbol: string, days: number = 1) =>
    api.get<MarketChartResponse>(
      `/api/crypto/market-chart/${symbol}/`,
      { params: { days } }
    ),

  // Global Metrics
  getGlobalMetrics: (params: { convert?: string } = {}) => {
    return api.get('/api/crypto/global-metrics/', { params });
  },
  
  // Price Performance
  getPricePerformance: (symbol: string, params: CryptoQuoteParams = {}) => {
    return api.get(`/api/crypto/performance/${symbol}/`, { params });
  },
  
  // Market Pairs
  getMarketPairs: (symbol: string, params: { limit?: string } = {}) => {
    return api.get(`/api/crypto/market-pairs/${symbol}/`, { params });
  },
};

// Authentication API
export const authAPI = {
  register: (data: FormData | {
    username: string;
    email: string;
    password: string;
    password2: string;
    first_name?: string;
    last_name?: string;
  }) => {
    if (data instanceof FormData) {
      return api.post('/api/auth/register/', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }
    return api.post('/api/auth/register/', data);
  },

  login: (username: string, password: string, twoFactorCode?: string, backupCode?: string) => {
    return api.post('/api/auth/login/', { 
      username, 
      password,
      two_factor_code: twoFactorCode,
      backup_code: backupCode
    });
  },

  googleOAuthVerify2FA: (twoFactorToken: string, twoFactorCode?: string, backupCode?: string) => {
    return api.post('/api/auth/google/verify-2fa/', {
      two_factor_token: twoFactorToken,
      two_factor_code: twoFactorCode,
      backup_code: backupCode,
    });
  },

  verifyEmail: (user_id: number, code: string) => {
    return api.post('/api/auth/verify-email/', { user_id, code });
  },

  resendCode: (user_id?: number, email?: string) => {
    return api.post('/api/auth/resend-code/', { user_id, email });
  },

  getProfile: () => {
    return api.get('/api/auth/profile/');
  },

  forgotPassword: (email: string) => {
    return api.post('/api/auth/forgot-password/', { email });
  },

  resetPassword: (email: string, code: string, new_password: string) => {
    return api.post('/api/auth/reset-password/', { email, code, new_password });
  },

  // Google OAuth
  getGoogleOAuthUrl: (redirectUri?: string) => {
    return api.get('/api/auth/google/url/', { 
      params: { redirect_uri: redirectUri || window.location.origin } 
    });
  },

  googleOAuthCallback: (code: string, redirectUri?: string) => {
    return api.post('/api/auth/google/callback/', { 
      code, 
      redirect_uri: redirectUri || window.location.origin 
    });
  },
};

// Predictions API
export interface PredictionUsage {
  plan: string;
  limit: number;
  used_today: number;
  remaining_today: number;
  ai_today: number;
  ml_today: number;
  candle_today: number;
  is_over_limit: boolean;
}

export const predictionsAPI = {
  create: (data: {
    coin_symbol: string;
    target_date: string;
    prediction_type?: string;
  }) => {
    return api.post('/api/predictions/create/', data);
  },

  getUsage: () => {
    return api.get<PredictionUsage>('/api/predictions/usage/');
  },

  getAll: () => {
    return api.get('/api/predictions/');
  },

  get: (predictionId: number) => {
    return api.get(`/api/predictions/${predictionId}/`);
  },
};

// ML Predictions API
export const mlPredictionsAPI = {
  predict: (data: {
    coin_symbol: string;
    days: number;
  }) => {
    return api.post('/api/predictions/ml-predict/', data);
  },
  getAll: () => {
    return api.get('/api/predictions/ml/');
  },
  get: (predictionId: number) => {
    return api.get(`/api/predictions/ml/${predictionId}/`);
  },
  delete: (predictionId: number) => {
    return api.delete(`/api/predictions/ml/${predictionId}/delete/`);
  },
};

// Alerts API
export const alertsAPI = {
  create: (data: {
    coin_symbol: string;
    alert_type: 'above' | 'below';
    target_price: number;
  }) => {
    return api.post('/api/alerts/create/', data);
  },

  getAll: () => {
    return api.get('/api/alerts/');
  },

  delete: (alertId: number) => {
    return api.delete(`/api/alerts/${alertId}/delete/`);
  },
};

// Candle Predictions API
export const candlePredictionsAPI = {
  create: (formData: FormData) => {
    return api.post('/api/candle-predictions/create/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  getAll: () => {
    return api.get('/api/candle-predictions/');
  },

  delete: (predictionId: number) => {
    return api.delete(`/api/candle-predictions/${predictionId}/delete/`);
  },
};

export interface HistoricalPriceParams {
  coin_symbol: string;
  date: string; // YYYY-MM-DD
  time?: string; // HH:MM
}

export const historicalPriceAPI = {
  get: (params: HistoricalPriceParams) => api.get('/api/historical-price/', { params }),
  save: () => api.post('/api/historical-price/save/'),
};

// Chatbot API
export const chatbotAPI = {
  query: (question: string) => {
    return api.post('/api/chatbot/query/', { question });
  },
};

// Profile API
export const profileAPI = {
  update: (formData: FormData) => {
    return api.put('/api/profile/update/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  updateTheme: (theme: 'light' | 'dark') => {
    return api.put('/api/profile/update-theme/', { theme });
  },
  changePassword: (data: {
    old_password: string;
    new_password: string;
    new_password2: string;
  }) => {
    return api.post('/api/profile/change-password/', data);
  },
  deleteAccount: (data: { password: string; confirmation: string }) => {
    return api.post('/api/profile/delete-account/', data);
  },
};

// 2FA API
export const twoFactorAPI = {
  setup: () => {
    return api.post('/api/2fa/setup/');
  },
  verifySetup: (code: string) => {
    return api.post('/api/2fa/verify-setup/', { code });
  },
  disable: (password: string) => {
    return api.post('/api/2fa/disable/', { password });
  },
  verifyLogin: (code?: string, backup_code?: string) => {
    return api.post('/api/2fa/verify-login/', { code, backup_code });
  },
};

// Coin Comparisons API
export const comparisonAPI = {
  save: (data: {
    name: string;
    coins_data: any[];
    is_favorite?: boolean;
  }) => {
    return api.post('/api/comparisons/save/', data);
  },

  getAll: () => {
    return api.get('/api/comparisons/');
  },

  get: (comparisonId: number) => {
    return api.get(`/api/comparisons/${comparisonId}/`);
  },

  update: (comparisonId: number, data: {
    name?: string;
    coins_data?: any[];
    is_favorite?: boolean;
  }) => {
    return api.put(`/api/comparisons/${comparisonId}/update/`, data);
  },

  delete: (comparisonId: number) => {
    return api.delete(`/api/comparisons/${comparisonId}/delete/`);
  },

  toggleFavorite: (comparisonId: number) => {
    return api.post(`/api/comparisons/${comparisonId}/toggle-favorite/`);
  },

  saveSnapshot: (comparisonId: number) => {
    return api.post(`/api/comparisons/${comparisonId}/snapshot/`);
  },

  getHistory: (comparisonId: number) => {
    return api.get(`/api/comparisons/${comparisonId}/history/`);
  },
};

// Contact Form API
export const contactAPI = {
  submit: (data: {
    name: string;
    email: string;
    subject: string;
    message: string;
  }) => {
    return api.post('/api/contact/', data);
  },
};

// Subscriptions API
export const subscriptionAPI = {
  createCheckout: (data: { plan: string; post_trial_plan?: string }) => {
    const redirect_origin = window.location.origin;
    return api.post('/api/subscriptions/checkout/', {
      ...data,
      redirect_origin,
    });
  },
  
  getStatus: () => {
    return api.get('/api/subscriptions/status/');
  },

  /** Call after user returns from Stripe success URL with session_id (sends confirmation email if webhook didn't run) */
  completeSession: (sessionId: string) => {
    return api.post('/api/subscriptions/complete-session/', { session_id: sessionId });
  },

  getInvoices: () => {
    return api.get('/api/subscriptions/invoices/');
  },
  
  cancel: () => {
    return api.post('/api/subscriptions/cancel/');
  },
  
  reactivate: () => {
    return api.post('/api/subscriptions/reactivate/');
  },
  
  createBillingPortal: () => {
    return api.post('/api/subscriptions/billing-portal/');
  },
};

// DEX (Swap) API – Phase 1 (Polygon) + Phase 2 (multi-chain)
export type DexChainConfig = {
  chain_id: number;
  chain_name: string;
  rpc_url: string;
  explorer_url?: string;
  native_symbol?: string;
};

export type DexConfigResponse =
  | { chains: DexChainConfig[]; default_slippage_percent: number }
  | (DexChainConfig & { default_slippage_percent: number; explorer_url: string; native_symbol: string });

export const dexAPI = {
  /** Get list of supported chains (no param) or single chain config (?chain_id=137). */
  getConfig: (chainId?: number) =>
    api.get<DexConfigResponse>(
      '/api/dex/config/',
      chainId != null ? { params: { chain_id: chainId } } : undefined
    ),

  getTokens: (chainId: number = 137) =>
    api.get<{ tokens?: Array<{ symbol: string; name: string; address: string; decimals: number; logoURI?: string }> }>(
      '/api/dex/tokens/',
      { params: { chain_id: chainId } }
    ),

  /** Batch token logo URLs for a chain. Returns { "0xaddr": "https://..." }. One call instead of N. */
  getTokenLogosBatch: (chainId: number, addresses: string[]) =>
    api.post<Record<string, string>>('/api/dex/token-logos/', { chain_id: chainId, addresses }),

  getQuote: (params: { src: string; dst: string; amount: string; from_address?: string; chain_id?: number }) =>
    api.get('/api/dex/quote/', {
      params: {
        src: params.src,
        dst: params.dst,
        amount: params.amount,
        from_address: params.from_address || undefined,
        chain_id: params.chain_id ?? undefined,
      },
    }),

  /** Cross-chain quote via LiFi. Returns step with estimate and transactionRequest. */
  getCrossChainQuote: (params: {
    from_chain: number;
    to_chain: number;
    from_token: string;
    to_token: string;
    from_amount: string;
    from_address: string;
    slippage?: number;
  }) =>
    api.get('/api/dex/cross-chain-quote/', {
      params: {
        from_chain: params.from_chain,
        to_chain: params.to_chain,
        from_token: params.from_token,
        to_token: params.to_token,
        from_amount: params.from_amount,
        from_address: params.from_address,
        slippage: params.slippage ?? undefined,
      },
    }),

  getSwap: (params: {
    src: string;
    dst: string;
    amount: string;
    from_address: string;
    slippage?: number | string;
    chain_id?: number;
  }) =>
    api.get('/api/dex/swap/', {
      params: {
        src: params.src,
        dst: params.dst,
        amount: params.amount,
        from_address: params.from_address,
        slippage: params.slippage ?? undefined,
        chain_id: params.chain_id ?? undefined,
      },
    }),

  getApproveCalldata: (tokenAddress: string, amount?: string, chainId?: number, spender?: string) =>
    api.get<{ data?: string; to?: string; value?: string; gas?: string }>('/api/dex/approve-calldata/', {
      params: {
        token_address: tokenAddress,
        amount: amount ?? undefined,
        chain_id: chainId ?? undefined,
        spender: spender ?? undefined,
      },
    }),

  getAllowance: (tokenAddress: string, walletAddress: string, chainId?: number) =>
    api.get<{ allowance?: string }>('/api/dex/allowance/', {
      params: {
        token_address: tokenAddress,
        wallet_address: walletAddress,
        chain_id: chainId ?? undefined,
      },
    }),

  broadcast: (signedTx: string, chainId: number = 137) =>
    api.post<{ tx_hash: string; explorer_url: string }>('/api/dex/broadcast/', {
      signed_tx: signedTx,
      chain_id: chainId,
    }),

  getTransactions: (address: string, chainId?: number, limit?: number, allChains?: boolean) =>
    api.get<{ result: Array<{ hash: string; from?: string; to?: string; value: string; timestamp?: string; blockNumber?: string; txreceipt_status?: string; explorerUrl: string; chainId?: number; chainName?: string; activityType?: string }>; explorerBase?: string }>(
      '/api/dex/transactions/',
      { params: { address, chain_id: chainId ?? undefined, limit: limit ?? undefined, all_chains: allChains ? '1' : undefined } }
    ),

  getPortfolioSummary: (address: string) =>
    api.get<{ totalUsd: number; byChain: Array<{ chainId: number; chainName: string; usdValue: number; assets: Array<{ symbol: string; balance: number; usdValue: number }> }> }>(
      '/api/dex/portfolio-summary/',
      { params: { address } }
    ),

  saveSwap: (payload: {
    wallet_address: string;
    from_chain_id: number;
    from_chain_name: string;
    to_chain_id: number;
    to_chain_name: string;
    from_token_symbol: string;
    from_token_address: string;
    to_token_symbol: string;
    to_token_address: string;
    from_amount: string;
    to_amount: string;
    from_amount_usd?: number;
    to_amount_usd?: number;
    gas_used?: string;
    gas_price?: string;
    tx_hash: string;
    explorer_url: string;
    slippage_percent?: number;
  }) =>
    api.post<{ id: number; tx_hash: string; created_at: string }>('/api/dex/save-swap/', payload),

  getSwapHistory: (address: string) =>
    api.get<{ results: Array<{
      id: number;
      wallet_address: string;
      from_chain_id: number;
      from_chain_name: string;
      to_chain_id: number;
      to_chain_name: string;
      from_token_symbol: string;
      from_token_address: string;
      to_token_symbol: string;
      to_token_address: string;
      from_amount: string;
      to_amount: string;
      from_amount_usd: number | null;
      to_amount_usd: number | null;
      gas_used: string | null;
      gas_price: string | null;
      tx_hash: string;
      explorer_url: string;
      slippage_percent: number | null;
      created_at: string;
    }> }>('/api/dex/swap-history/', { params: { address } }),
};

// MoonPay (Fiat On-Ramp / Off-Ramp) API
export interface MoonPayConfigResponse {
  configured: boolean;
  is_sandbox: boolean;
  public_key: string;
}

export interface MoonPayTransactionRow {
  id: number;
  moonpay_transaction_id: string;
  external_transaction_id: string;
  transaction_type: 'buy' | 'sell';
  status: string;
  crypto_currency: string;
  crypto_amount: string | null;
  fiat_currency: string;
  fiat_amount: string | null;
  fee_amount: string | null;
  network: string;
  wallet_address: string;
  failure_reason: string;
  created_at: string;
  updated_at: string;
}

export const moonpayAPI = {
  getConfig: () => api.get<MoonPayConfigResponse>('/api/moonpay/config/'),

  signUrl: (url: string) =>
    api.post<{ signature: string }>('/api/moonpay/sign-url/', { url }),

  getBuyUrl: (params: {
    wallet_address: string;
    currency_code?: string;
    base_currency_code?: string;
    base_currency_amount?: string;
    email?: string;
    theme_id?: string;
    color_code?: string;
    redirect_url?: string;
    show_only_currencies?: string;
    payment_method?: string;
  }) =>
    api.get<{ url: string; external_transaction_id: string; is_sandbox: boolean }>(
      '/api/moonpay/buy-url/',
      { params }
    ),

  getSellUrl: (params: {
    refund_wallet_address: string;
    default_currency_code?: string;
    quote_currency_code?: string;
    base_currency_amount?: string;
    email?: string;
    theme_id?: string;
    color_code?: string;
    redirect_url?: string;
    show_only_currencies?: string;
  }) =>
    api.get<{ url: string; external_transaction_id: string; is_sandbox: boolean }>(
      '/api/moonpay/sell-url/',
      { params }
    ),

  getTransactions: (params?: { type?: 'buy' | 'sell'; limit?: number }) =>
    api.get<{ results: MoonPayTransactionRow[] }>('/api/moonpay/transactions/', {
      params,
    }),
};

// Forum / Community API
export interface ForumCategory {
  id: number;
  name: string;
  slug: string;
  description: string;
  sort_order: number;
  post_count: number;
}
export interface ForumUserBrief {
  id: number;
  username: string;
  display_name: string;
  profile_picture_url?: string | null;
  is_staff?: boolean;
}
export interface ForumComment {
  id: number;
  post: number;
  user: ForumUserBrief;
  parent: number | null;
  body: string;
  attachments?: ForumAttachment[];
  created_at: string;
  updated_at: string;
  replies: ForumComment[];
}
export interface ForumAttachment {
  id: number;
  file: string;
  file_url: string;
  caption: string;
  sort_order: number;
  created_at: string;
}
export interface ForumPost {
  id: number;
  user: ForumUserBrief;
  category: ForumCategory;
  title: string;
  body: string;
  status: string;
  views: number;
  is_pinned: boolean;
  is_locked: boolean;
  attachments?: ForumAttachment[];
  created_at: string;
  updated_at: string;
  comment_count?: number;
  like_count?: number;
  liked?: boolean;
  comments?: ForumComment[];
  approved_at?: string | null;
}
export const forumAPI = {
  getCategories: () => api.get<ForumCategory[]>('/api/forum/categories/'),
  getPosts: (params?: { category?: string; search?: string; page?: number; page_size?: number }) =>
    api.get<{ results: ForumPost[]; count: number; page: number; page_size: number }>('/api/forum/posts/', { params }),
  getPost: (id: number) => api.get<ForumPost>(`/api/forum/posts/${id}/`),
  createPost: (data: { category: number; title: string; body: string }, files?: File[]) => {
    if (files?.length) {
      const form = new FormData();
      form.append('category', String(data.category));
      form.append('title', data.title);
      form.append('body', data.body);
      files.forEach((f) => form.append('attachments', f));
      return api.post<ForumPost>('/api/forum/posts/create/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return api.post<ForumPost>('/api/forum/posts/create/', data);
  },
  updatePost: (id: number, data: { category?: number; title?: string; body?: string }, files?: File[]) => {
    if (files?.length) {
      const form = new FormData();
      if (data.category != null) form.append('category', String(data.category));
      if (data.title != null) form.append('title', data.title);
      if (data.body != null) form.append('body', data.body);
      files.forEach((f) => form.append('attachments', f));
      return api.put<ForumPost>(`/api/forum/posts/${id}/update/`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return api.put<ForumPost>(`/api/forum/posts/${id}/update/`, data);
  },
  deletePost: (id: number) => api.delete(`/api/forum/posts/${id}/delete/`),
  likePost: (postId: number) => api.post<{ liked: boolean; like_count: number }>(`/api/forum/posts/${postId}/like/`),
  myPosts: () => api.get<ForumPost[]>('/api/forum/posts/my-posts/'),
  createComment: (postId: number, data: { body: string; parent?: number }, imageFile?: File) => {
    if (imageFile) {
      const form = new FormData();
      form.append('body', data.body);
      if (data.parent != null) form.append('parent', String(data.parent));
      form.append('attachments', imageFile);
      return api.post<ForumComment>(`/api/forum/posts/${postId}/comments/`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return api.post<ForumComment>(`/api/forum/posts/${postId}/comments/`, data);
  },
  updateComment: (id: number, body: string) => api.put<ForumComment>(`/api/forum/comments/${id}/`, { body }),
  deleteComment: (id: number) => api.delete(`/api/forum/comments/${id}/delete/`),
  deletePostAttachment: (postId: number, attachmentId: number) =>
    api.delete(`/api/forum/posts/${postId}/attachments/${attachmentId}/`),
  deleteCommentAttachment: (commentId: number, attachmentId: number) =>
    api.delete(`/api/forum/comments/${commentId}/attachments/${attachmentId}/`),
};

// CoinGecko API (Public - no auth required)
export const coinGeckoAPI = {
  // Get global market data
  getGlobalData: async () => {
    const response = await axios.get('https://api.coingecko.com/api/v3/global');
    return response.data;
  },

  // Get top cryptocurrencies by market cap
  getTopCryptos: async (limit: number = 3) => {
    const response = await axios.get(
      `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=${limit}&page=1&sparkline=false`
    );
    return response.data;
  },
};

export default api;

